import { Controller } from "@hotwired/stimulus";
import Sortable from "sortablejs";

// Live editor for a Pitch.
//
// Architecture (and why):
//   - Form on the left, iframe on the right pointing at /pitches/:id/preview.
//   - Form fields trigger a debounced auto-save via PATCH /pitches/:id.json.
//   - On every successful save we refresh the iframe so the preview
//     stays in sync. No server-rendered partial swap, no Turbo Frame
//     gymnastics — the editor stays mounted, dropdowns stay alive,
//     focus doesn't jump.
//   - Per-creator notes save individually on blur (or after a short
//     idle), independent of the main form.
//   - Drag-to-reorder uses SortableJS; positions persist via
//     POST /pitch_memberships/reorder.
//
// Forward-looking note: when PR #720's chat editor lands, this same
// controller would shrink (or be replaced) to host the chat input on
// the left and keep the iframe on the right.
export default class extends Controller {
  static targets = [
    "form",
    "previewFrame",
    "fontSelect",
    "creatorsList",
    "creator",
    "noteField",
    "autosave",
    "pulse",
    "statusLabel",
    "draftActions",
    "publishedActions",
    "publicUrlInput",
    "publicLink",
  ];

  static values = {
    updateUrl: String,
    publishUrl: String,
    unpublishUrl: String,
    previewUrl: String,
    publicUrl: String,
    reorderUrl: String,
  };

  connect() {
    this.csrfToken = document
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute("content");
    this.saveTimer = null;
    this.noteTimers = new Map();
    this.pendingNotes = new Set();
    this.membershipTimers = new Map();
    this._initFontDropdown();
    this._initSortable();
    // The status modal is moved to <body> by Fomantic when shown, so it's no
    // longer a descendant of this controller's element — Stimulus action
    // bindings inside it wouldn't resolve. Listen for window-level custom
    // events dispatched by the modal buttons instead.
    this._onPublishEvent = () => this.publish();
    this._onUnpublishEvent = () => this.unpublish();
    window.addEventListener("pitch-editor:publish", this._onPublishEvent);
    window.addEventListener("pitch-editor:unpublish", this._onUnpublishEvent);
  }

  disconnect() {
    window.removeEventListener("pitch-editor:publish", this._onPublishEvent);
    window.removeEventListener(
      "pitch-editor:unpublish",
      this._onUnpublishEvent
    );
  }

  // ----- main form auto-save -------------------------------------------

  scheduleSave() {
    clearTimeout(this.saveTimer);
    this._showAutosaveStatus("typing");
    this.saveTimer = setTimeout(() => this._save(), 500);
  }

  async _save() {
    if (!this.hasFormTarget) return;
    const formData = new FormData(this.formTarget);
    try {
      const res = await fetch(this.updateUrlValue, {
        method: "PATCH",
        headers: { Accept: "application/json", "X-CSRF-Token": this.csrfToken },
        body: formData,
      });
      if (!res.ok) throw new Error(`save failed (${res.status})`);
      this._refreshPreview();
      this._showAutosaveStatus("saved");
    } catch (e) {
      console.error("[pitch-editor] save failed", e);
      this._showAutosaveStatus("error");
    }
  }

  // ----- per-creator note auto-save ------------------------------------

  scheduleNoteSave(event) {
    const ta = event.target;
    const id = ta.dataset.membershipId;
    if (!id) return;
    this.pendingNotes.add(id);
    if (this.noteTimers.has(id)) clearTimeout(this.noteTimers.get(id));
    this._showAutosaveStatus("typing");
    this.noteTimers.set(
      id,
      setTimeout(() => this._saveNote(ta), 700)
    );
  }

  async saveNoteNow(event) {
    const ta = event.target;
    const id = ta.dataset.membershipId;
    if (!id || !this.pendingNotes.has(id)) return;
    if (this.noteTimers.has(id)) clearTimeout(this.noteTimers.get(id));
    await this._saveNote(ta);
  }

  async _saveNote(ta) {
    const id = ta.dataset.membershipId;
    if (!id) return;
    const url = `/pitches/${this._pitchId()}/pitch_memberships/${id}`;
    try {
      const res = await fetch(url, {
        method: "PATCH",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken,
        },
        body: JSON.stringify({ pitch_membership: { note: ta.value } }),
      });
      if (!res.ok) throw new Error(`note save failed (${res.status})`);
      this.pendingNotes.delete(id);
      this._refreshPreview();
      this._showAutosaveStatus(
        this.pendingNotes.size === 0 ? "saved" : "typing"
      );
    } catch (e) {
      console.error("[pitch-editor] note save failed", e);
      this._showAutosaveStatus("error");
    }
  }

  // ----- per-creator field auto-save (display name, demographics, links, etc.) -----

  scheduleMembershipSave(event) {
    const creator = event.target.closest("[data-membership-id]");
    if (!creator) return;
    const id = creator.dataset.membershipId;
    if (!id) return;
    if (this.membershipTimers.has(id))
      clearTimeout(this.membershipTimers.get(id));
    this._showAutosaveStatus("typing");
    this.membershipTimers.set(
      id,
      setTimeout(() => this._saveMembership(creator), 500)
    );
  }

  async _saveMembership(creator) {
    const id = creator.dataset.membershipId;
    if (!id) return;
    const payload = this._collectMembershipPayload(creator);
    const url = `/pitches/${this._pitchId()}/pitch_memberships/${id}`;
    try {
      const res = await fetch(url, {
        method: "PATCH",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken,
        },
        body: JSON.stringify({ pitch_membership: payload }),
      });
      if (!res.ok) throw new Error(`membership save failed (${res.status})`);
      // Update the summary text in place so the closed accordion stays in sync.
      const summaryName = creator.querySelector(
        ".pitch-editor__creator-summary-text > strong"
      );
      const newName = creator.querySelector('[data-field="display_name"]')
        ?.value;
      if (summaryName && newName) summaryName.textContent = newName;
      this._refreshPreview();
      this._showAutosaveStatus("saved");
    } catch (e) {
      console.error("[pitch-editor] membership save failed", e);
      this._showAutosaveStatus("error");
    }
  }

  _collectMembershipPayload(creator) {
    const payload = {};
    creator.querySelectorAll("[data-field]").forEach((el) => {
      const name = el.dataset.field;
      // Multi-row collection fields are read separately below.
      if (
        name === "profile_links" ||
        name === "content_sample_links" ||
        name === "custom_fields"
      )
        return;
      payload[name] = el.value;
    });
    payload.profile_links = this._readRows(creator, "profile_links", [
      "provider",
      "handle",
      "url",
    ]);
    payload.content_sample_links = this._readRows(
      creator,
      "content_sample_links",
      ["label", "url"]
    );
    payload.custom_fields = this._readRows(creator, "custom_fields", [
      "label",
      "value",
    ]);
    return payload;
  }

  _readRows(creator, group, keys) {
    const rows = creator.querySelectorAll(
      `[data-pitch-editor-target="rowGroup"][data-group="${group}"] .pitch-editor__row`
    );
    return Array.from(rows)
      .map((row) => {
        const obj = {};
        keys.forEach((k) => {
          const el = row.querySelector(`[data-key="${k}"]`);
          obj[k] = el ? el.value.trim() : "";
        });
        return obj;
      })
      .filter((obj) => Object.values(obj).some((v) => v !== ""));
  }

  addRow(event) {
    const btn = event.currentTarget;
    const group = btn.dataset.group;
    const creator = btn.closest("[data-membership-id]");
    if (!creator) return;
    const container = creator.querySelector(
      `[data-pitch-editor-target="rowGroup"][data-group="${group}"]`
    );
    if (!container) return;
    const template = container.querySelector(".pitch-editor__row");
    if (!template) return;
    const clone = template.cloneNode(true);
    clone.querySelectorAll("input").forEach((i) => (i.value = ""));
    clone.querySelectorAll("select").forEach((s) => (s.selectedIndex = 0));
    container.appendChild(clone);
  }

  removeRow(event) {
    event.preventDefault();
    const btn = event.currentTarget;
    const row = btn.closest(".pitch-editor__row");
    const container = row?.parentElement;
    if (!row || !container) return;
    // Always keep at least one row to act as a template. Clear it instead of removing.
    if (container.querySelectorAll(".pitch-editor__row").length <= 1) {
      row.querySelectorAll("input").forEach((i) => (i.value = ""));
      row.querySelectorAll("select").forEach((s) => (s.selectedIndex = 0));
    } else {
      row.remove();
    }
    const creator = btn.closest("[data-membership-id]");
    if (creator) this._saveMembership(creator);
  }

  // ----- publish / unpublish -------------------------------------------

  async publish() {
    await this._stateAction(this.publishUrlValue);
  }

  async unpublish() {
    if (!confirm("Hide the public link? You can republish later.")) return;
    await this._stateAction(this.unpublishUrlValue);
  }

  async _stateAction(url) {
    try {
      const res = await fetch(url, {
        method: "POST",
        headers: { Accept: "application/json", "X-CSRF-Token": this.csrfToken },
      });
      if (!res.ok) throw new Error(`state action failed (${res.status})`);
      const data = await res.json();
      this._applyStateUpdate(data);
      this._refreshPreview();
    } catch (e) {
      console.error("[pitch-editor] state action failed", e);
      alert("Couldn't update — try again or refresh.");
    }
  }

  _applyStateUpdate(data) {
    if (this.hasStatusLabelTarget) {
      this.statusLabelTarget.textContent =
        data.status.charAt(0).toUpperCase() + data.status.slice(1);
      this.statusLabelTarget.className =
        "ui label tiny " +
        (data.published
          ? "green"
          : data.status === "archived"
          ? "grey"
          : "yellow");
    }
    if (this.hasDraftActionsTarget) {
      this.draftActionsTarget.style.display = data.published ? "none" : "";
    }
    if (this.hasPublishedActionsTarget) {
      this.publishedActionsTarget.style.display = data.published ? "" : "none";
    }
    if (data.public_url) {
      if (this.hasPublicUrlInputTarget)
        this.publicUrlInputTarget.value = data.public_url;
      if (this.hasPublicLinkTarget)
        this.publicLinkTarget.href = data.public_url;
    }
  }

  // ----- drag-to-reorder ----------------------------------------------

  _initSortable() {
    if (!this.hasCreatorsListTarget) return;
    this.sortable = new Sortable(this.creatorsListTarget, {
      handle: ".pitch-editor__creator-handle",
      animation: 150,
      ghostClass: "is-dragging",
      onEnd: () => this._persistOrder(),
    });
  }

  async _persistOrder() {
    const ids = Array.from(
      this.creatorsListTarget.querySelectorAll("[data-id]")
    ).map((el) => el.dataset.id);
    try {
      this._showAutosaveStatus("typing");
      const res = await fetch(this.reorderUrlValue, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken,
        },
        body: JSON.stringify({ ids }),
      });
      if (!res.ok) throw new Error(`reorder failed (${res.status})`);
      this._refreshPreview();
      this._showAutosaveStatus("saved");
    } catch (e) {
      console.error("[pitch-editor] reorder failed", e);
      this._showAutosaveStatus("error");
    }
  }

  // ----- instant preview: accent color + section toggles ---------------
  //
  // These two fields don't wait for the debounced save/reload cycle below —
  // they postMessage straight into the preview iframe, which applies the
  // change to its own DOM immediately (see the listener rendered by
  // public_pitches/show.html.haml when preview: true). The debounced save
  // still runs on the same input (via the form-level scheduleSave binding)
  // so the change persists; this just makes the PREVIEW stop waiting on it.

  previewAccentColor(event) {
    this._postToPreview({accentColor: event.target.value});
  }

  toggleSection(event) {
    const key = event.target.dataset.sectionKey;
    if (!key) return;
    this._postToPreview({section: key, visible: event.target.checked});
  }

  _postToPreview(message) {
    if (!this.hasPreviewFrameTarget) return;
    try {
      this.previewFrameTarget.contentWindow.postMessage(
        {source: "pitch-editor", ...message},
        window.location.origin
      );
    } catch (e) {
      // Iframe not ready yet — the debounced save's reload will catch it up.
    }
  }

  // ----- preview iframe -----------------------------------------------

  _refreshPreview() {
    if (!this.hasPreviewFrameTarget) return;
    if (this.hasPulseTarget) this.pulseTarget.classList.add("is-active");
    const frame = this.previewFrameTarget;
    const url = new URL(this.previewUrlValue, window.location.origin);
    url.searchParams.set("_t", Date.now());
    // Use replace() so we don't pollute iframe history with cache-busts.
    try {
      frame.contentWindow.location.replace(url.toString());
    } catch (e) {
      frame.src = url.toString();
    }
    setTimeout(() => {
      if (this.hasPulseTarget) this.pulseTarget.classList.remove("is-active");
    }, 700);
  }

  // ----- helpers ------------------------------------------------------

  _initFontDropdown() {
    if (window.jQuery && this.hasFontSelectTarget) {
      window.jQuery(this.fontSelectTarget).dropdown({
        onChange: () => this.scheduleSave(),
      });
    }
  }

  _showAutosaveStatus(state) {
    if (!this.hasAutosaveTarget) return;
    this.autosaveTarget.dataset.state = state;
    this.autosaveTarget.textContent =
      state === "typing"
        ? "Saving…"
        : state === "saved"
        ? "Saved"
        : state === "error"
        ? "Save failed — try again"
        : "";
  }

  _pitchId() {
    // updateUrlValue looks like "/pitches/123.json"
    const m = this.updateUrlValue.match(/\/pitches\/(\d+)/);
    return m ? m[1] : null;
  }
}
